import React from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  useTheme,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import { useTranslation } from "react-i18next";
import { KpiCard } from "../../../types/analytics";

interface KpiStatCardProps {
  title: string;
  data: KpiCard;
  prefix?: string;
  suffix?: string;
  icon?: React.ReactNode;
  color?: string;
}

const formatValue = (value: number, prefix?: string, suffix?: string) => {
  let formatted: string;
  if (value >= 1000000) {
    formatted = (value / 1000000).toFixed(1) + "M";
  } else if (value >= 1000) {
    formatted = (value / 1000).toFixed(1) + "K";
  } else {
    formatted = value % 1 === 0 ? value.toString() : value.toFixed(2);
  }
  return `${prefix || ""}${formatted}${suffix || ""}`;
};

const KpiStatCard: React.FC<KpiStatCardProps> = ({
  title,
  data,
  prefix,
  suffix,
  icon,
  color,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const isUp = data.changeDirection === "up";
  const isDown = data.changeDirection === "down";
  const hasComparison = isUp || isDown || data.changePercentage !== 0;
  const changeColor = isUp
    ? theme.palette.success.main
    : isDown
      ? theme.palette.error.main
      : theme.palette.text.secondary;

  return (
    <Card
      sx={{
        height: "100%",
        background: color
          ? `linear-gradient(135deg, ${color}15 0%, ${color}05 100%)`
          : undefined,
        border: color ? `1px solid ${color}30` : undefined,
      }}
    >
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            mb: 1,
          }}
        >
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontWeight: 500, fontSize: "0.8rem" }}
          >
            {title}
          </Typography>
          {icon && (
            <Box sx={{ color: color || "primary.main", opacity: 0.7 }}>
              {icon}
            </Box>
          )}
        </Box>

        <Typography
          variant="h4"
          sx={{ fontWeight: 700, mb: 1, fontSize: "1.75rem" }}
        >
          {formatValue(data.currentValue, prefix, suffix)}
        </Typography>

        {hasComparison && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            {isUp && <TrendingUpIcon sx={{ fontSize: 18, color: changeColor }} />}
            {isDown && (
              <TrendingDownIcon sx={{ fontSize: 18, color: changeColor }} />
            )}
            {!isUp && !isDown && (
              <TrendingFlatIcon sx={{ fontSize: 18, color: changeColor }} />
            )}
            <Typography
              variant="body2"
              sx={{ color: changeColor, fontWeight: 600, fontSize: "0.8rem" }}
            >
              {data.changePercentage > 0 ? "+" : ""}
              {data.changePercentage}%
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: "0.75rem" }}
            >
              {t("admin.analytics.vsPreviousPeriod")}
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default KpiStatCard;
